import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadAuth, loadFirestore } from '../lazyFirebase';

const AuthContext = createContext();

// ─── Business Config (apna .env mein set karo) ───────────────────────────────
const BUSINESS_NAME   = process.env.REACT_APP_BUSINESS_NAME   || 'Organic Slim Fit';
const BUSINESS_EMAIL  = process.env.REACT_APP_BUSINESS_EMAIL  || 'orders@organicslimfit.com';
const SUPPORT_PHONE   = process.env.REACT_APP_SUPPORT_PHONE   || '923111100340';

const EMAILJS_SERVICE_ID      = process.env.REACT_APP_EMAILJS_SERVICE_ID        || '';
const EMAILJS_PUBLIC_KEY      = process.env.REACT_APP_EMAILJS_PUBLIC_KEY         || '';
const EMAILJS_VERIFY_TEMPLATE = process.env.REACT_APP_EMAILJS_VERIFY_TEMPLATE_ID || '';

// ─── EmailJS helper ───────────────────────────────────────────────────────────
export const sendViaEmailJS = async (templateId, params) => {
  const svcId  = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
  const pubKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || '';
  if (!svcId || !pubKey || !templateId) return;
  try {
    const res = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id:      svcId,
        template_id:     templateId,
        user_id:         pubKey,
        template_params: params,
      }),
    });
    if (!res.ok) console.warn('EmailJS error:', res.status);
  } catch (e) {
    console.warn('EmailJS send failed (non-fatal):', e.message);
  }
};
// ─────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const startAuthRef = React.useRef(null);

  // SPEED: the Firebase Auth SDK does not load on first paint. It loads on the first user interaction (scroll/touch/click)
  // or after 8 seconds. It loads immediately on login/checkout/profile.
  useEffect(() => {
    let unsub = () => {};
    let cancelled = false, started = false, timer;
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    const start = () => {
      if (started || cancelled) return;
      started = true;
      events.forEach(e => window.removeEventListener(e, start));
      clearTimeout(timer);
      loadAuth().then(({ auth, onAuthStateChanged }) => {
        if (cancelled) return;
        unsub = onAuthStateChanged(auth, (u) => { setUser(u); setAuthLoading(false); });
        loadFirestore().catch(() => {}); // ready in advance for order saving
      }).catch(() => setAuthLoading(false));
    };
    events.forEach(e => window.addEventListener(e, start, { passive: true, once: true }));
    timer = setTimeout(start, 8000);
    startAuthRef.current = start;
    return () => { cancelled = true; events.forEach(e => window.removeEventListener(e, start)); clearTimeout(timer); unsub(); };
  }, []);

  // ── Signup ────────────────────────────────────────────────────────────────
  const signup = async (email, password, name) => {
    const { auth, createUserWithEmailAndPassword, updateProfile, sendEmailVerification } = await loadAuth();
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: name });

    // Firebase built-in verification (backup)
    await sendEmailVerification(cred.user, { url: window.location.origin });

    // Apni business email se branded verification email
    await sendViaEmailJS(EMAILJS_VERIFY_TEMPLATE, {
      to_email:     email,
      to_name:      name || email,
      from_name:    BUSINESS_NAME,
      from_email:   BUSINESS_EMAIL,
      support_wa:   SUPPORT_PHONE,
      verify_link:  window.location.origin,
      business_name: BUSINESS_NAME,
    });

    return cred;
  };

  const login = async (email, password) => {
    const { auth, signInWithEmailAndPassword } = await loadAuth();
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    const { auth, signOut } = await loadAuth();
    return signOut(auth);
  };

  const forgotPassword = async (email) => {
    const { auth, sendPasswordResetEmail } = await loadAuth();
    return sendPasswordResetEmail(auth, email, { url: window.location.origin });
  };

  // ── Resend verification ───────────────────────────────────────────────────
  const resendVerificationEmail = async () => {
    const { auth, sendEmailVerification } = await loadAuth();
    await sendEmailVerification(auth.currentUser, { url: window.location.origin });
    await sendViaEmailJS(EMAILJS_VERIFY_TEMPLATE, {
      to_email:     auth.currentUser.email,
      to_name:      auth.currentUser.displayName || auth.currentUser.email,
      from_name:    BUSINESS_NAME,
      from_email:   BUSINESS_EMAIL,
      support_wa:   SUPPORT_PHONE,
      verify_link:  window.location.origin,
      business_name: BUSINESS_NAME,
    });
  };

  return (
    <AuthContext.Provider value={{
      user, authLoading,
      signup, login, logout, forgotPassword,
      resendVerificationEmail,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
